/*
 * ------------------------------------------------------------
 * Cart milestone progress bar (NH-??)
 *
 * One combined progress bar for the cart drawer + cart page that shows how
 * far the customer is from each reward milestone (e.g. FREE EXPRESS SHIPPING
 * -> FREE GIFT) along a single track.
 *
 * Display-only. BOGOs.io still owns actually adding the gift to the cart, so
 * this bar never mutates the cart — it only reads the subtotal and renders
 * progress. It replaces the native single free-shipping bar, which is turned
 * off via `cart_threshold_enable = false` in theme settings.
 *
 * Config comes from window.GM_STATE.cart.milestoneBar (see gm-state.liquid).
 * The bar is injected into the Liquid-owned drawer/cart wrappers (NOT the
 * Preact-controlled <mini-cart>/<cart-main> subtrees) so it survives cart
 * re-renders. Cart changes are detected by wrapping fetch/XHR to /cart/*.
 * ------------------------------------------------------------
 */
(function () {
  'use strict';

  var CFG = (window.GM_STATE && GM_STATE.cart && GM_STATE.cart.milestoneBar) || null;
  if (!CFG || !CFG.enable) return;

  var MILES = [];

  // --- Gift milestones from BOGOs -----------------------------------------
  //
  // BOGOs (Secomapp) publishes the shopper's eligible offers on
  // window.FGSECOMAPP.offers. That list is already narrowed by BOGOs to this
  // specific shopper — offers they can't claim (new-customers-only, expired,
  // not yet started) never appear in it. Every offer we see here is one BOGOs
  // will actually put in their cart, so the bar shows all of them. Bar and
  // cart agreeing is the entire point of the bar.
  //
  // Do NOT filter on `priority`. In BOGOs, priority is only a ranking for its
  // `stop_lower_priority` stacking rule ("if this offer applies, cancel the
  // ones ranked below it") — it is not a display flag. No Naked Harvest offer
  // currently sets stop_lower_priority against a real competitor, so the
  // priority numbers here carry no meaning at all. Filtering on priority == 1
  // silently hid the $100 new-customer Mystery Product (priority 2) from the
  // bar while BOGOs went on adding it to the cart.
  //
  // This reads BOGOs' internal page data, not a documented API. If the shape
  // ever changes we fall back to the manual gift milestone in theme settings.

  function offerLabel(offer) {
    // By default every gift reads the theme setting's generic label ("FREE
    // GIFT"), so the bar never names a gift that has run out (NH-39). The
    // per-offer names are opt-in via `useOfferNames`.
    if (!CFG.useOfferNames) return String(CFG.giftLabel || 'FREE GIFT').toUpperCase();

    // `offer_name` is the customer-facing line ("Spend $150 & receive a FREE
    // NH Gym Towel"); pull the reward out of it. `title` is the internal name
    // ("Gym Towel GWP") and is the fallback.
    var name = String(offer.offer_name || '');
    var m = name.match(/free\s+(.+?)\s*$/i);
    if (m && m[1]) return m[1].replace(/[.!]+$/, '').trim().toUpperCase();
    var title = String(offer.title || '').replace(/\bGWP\b/i, '').trim();
    return (title || 'FREE GIFT').toUpperCase();
  }

  // Returns null only when BOGOs data can't be read at all, which is the one
  // case the theme-setting fallback is for. BOGOs loaded with no gift offers
  // running returns [], so the bar shows just free shipping rather than a gift
  // nobody is giving away (the old gym towel, still showing after every offer
  // was switched off).
  function giftMilestonesFromBogos() {
    if (!window.FGSECOMAPP || !Array.isArray(FGSECOMAPP.offers)) return null;
    var offers = FGSECOMAPP.offers;
    var now = Date.now();
    var out = [];
    offers.forEach(function (o) {
      if (!o) return;
      // Belt and braces: BOGOs already excludes these, but this is its
      // internal state and the check is cheap.
      if (o.start_time && Date.parse(o.start_time) > now) return;
      if (o.end_time && Date.parse(o.end_time) < now) return;
      // Only spend-threshold offers belong on a $-based bar.
      var cond = (o.offer_conditions || [])[0];
      var min = cond && parseFloat(cond.min_amount);
      if (!min || min <= 0) return;
      out.push({ amount: min, label: offerLabel(o) });
    });
    return out;
  }

  function buildMilestones() {
    var list = [];

    // Free shipping stays a theme setting: it's a Shopify shipping rate, not
    // a BOGOs offer. Theme settings are entered in the shop's base currency.
    (CFG.milestones || []).forEach(function (m) {
      if (m && m.kind === 'shipping' && parseFloat(m.amount) > 0 && m.label) {
        list.push({ cents: themeCents(parseFloat(m.amount)), label: String(m.label) });
      }
    });

    var gifts = giftMilestonesFromBogos();
    if (!gifts) {
      // Fallback: manual gift milestone from theme settings (base currency).
      (CFG.milestones || []).forEach(function (m) {
        if (m && m.kind === 'gift' && parseFloat(m.amount) > 0 && m.label) {
          list.push({ cents: themeCents(parseFloat(m.amount)), label: String(m.label) });
        }
      });
    } else {
      // BOGOs amounts arrive in the ACTIVE currency — see bogosCents().
      gifts.forEach(function (g) {
        list.push({ cents: bogosCents(g.amount), label: g.label });
      });
    }

    // Merge milestones that share a threshold (e.g. free shipping and a gift
    // both at $100) into one stop, keeping each reward as its own line so the
    // node label stays readable instead of one long run-on string. Keyed on
    // converted cents so the two sources still line up in any currency.
    var byCents = {};
    list.forEach(function (m) {
      var k = String(m.cents);
      if (!byCents[k]) byCents[k] = { cents: m.cents, parts: [] };
      if (byCents[k].parts.indexOf(m.label) === -1) byCents[k].parts.push(m.label);
    });

    return Object.keys(byCents)
      .map(function (k) {
        var e = byCents[k];
        return { cents: e.cents, parts: e.parts, label: e.parts.join(' + ') };
      })
      .sort(function (a, b) { return a.cents - b.cents; });
  }

  var INTRO = CFG.intro || "You're [amount] away from [reward]";
  var SUCCESS = CFG.success || "You've unlocked every reward!";
  var MONEY_FORMAT = (GM_STATE.shopify && GM_STATE.shopify.moneyFormat) || '${{amount}}';

  function rate() {
    return (window.Shopify && Shopify.currency && Shopify.currency.rate)
      ? parseFloat(Shopify.currency.rate) : 1;
  }
  // Two sources, two scales — do not use one converter for both.
  //
  // Theme settings are entered in the shop's BASE currency, so they need the
  // rate applied. Mirrors the native bar's `100 * Math.round(threshold * rate)`.
  function themeCents(dollars) { return Math.round(dollars * rate()) * 100; }
  //
  // BOGOs runs its own convertMultiCurrency() over `min_amount` before putting
  // offers on the page, so its thresholds are ALREADY in the active currency.
  // Applying the rate again would convert them twice — invisible while the
  // rate is 1.0, silently wrong the moment a second market is enabled.
  function bogosCents(dollars) { return Math.round(dollars * 100); }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // Standard Shopify money formatter driven by the shop's money_format string.
  function formatMoney(cents) {
    cents = Math.max(0, Math.round(cents));
    function withDelimiters(number, precision, thousands, decimal) {
      thousands = thousands || ','; decimal = decimal || '.';
      precision = precision == null ? 2 : precision;
      if (isNaN(number) || number == null) return '0';
      number = (number / 100).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return dollars + (parts[1] ? decimal + parts[1] : '');
    }
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var match = MONEY_FORMAT.match(placeholder);
    var token = match ? match[1] : 'amount';
    var value;
    switch (token) {
      case 'amount_no_decimals': value = withDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_space_separator': value = withDelimiters(cents, 2, ' ', '.'); break;
      default: value = withDelimiters(cents, 2);
    }
    return MONEY_FORMAT.replace(placeholder, value);
  }

  function buildHTML(subtotalCents) {
    var maxCents = MILES[MILES.length - 1].cents;
    var fillPct = maxCents > 0 ? Math.max(0, Math.min(1, subtotalCents / maxCents)) * 100 : 0;

    // Headline names the next unreached reward, else the success message.
    var next = null;
    for (var i = 0; i < MILES.length; i++) {
      if (subtotalCents < MILES[i].cents) { next = MILES[i]; break; }
    }
    var headline;
    if (next) {
      var remaining = next.cents - subtotalCents;
      headline = INTRO
        .replace('[amount]', '<strong>' + formatMoney(remaining) + '</strong>')
        .replace('[reward]', '<strong>' + escapeHtml(next.label) + '</strong>');
    } else {
      headline = SUCCESS;
    }

    var nodes = MILES.map(function (m) {
      var reached = subtotalCents >= m.cents;
      var leftPct = maxCents > 0 ? (m.cents / maxCents) * 100 : 0;
      var align = leftPct < 12 ? 'left' : (leftPct > 88 ? 'right' : 'center');
      return '<div class="nh-ms__node' + (reached ? ' is-reached' : '') + '"' +
             ' data-align="' + align + '" style="left:' + leftPct + '%">' +
             '<span class="nh-ms__dot"></span>' +
             '<span class="nh-ms__label">' +
             (m.parts || [m.label]).map(function (p) {
               return '<span class="nh-ms__reward">' + escapeHtml(p) + '</span>';
             }).join('') +
             '</span>' +
             '</div>';
    }).join('');

    return '<p class="nh-ms__headline">' + headline + '</p>' +
           '<div class="nh-ms__track">' +
           '<div class="nh-ms__fill" style="width:' + fillPct + '%"></div>' +
           '<div class="nh-ms__nodes">' + nodes + '</div>' +
           '</div>';
  }

  var STYLE_ID = 'nh-ms-style';
  function injectStyle() {
    if (document.getElementById(STYLE_ID)) return;
    var css =
      '.nh-ms{--nh-accent:rgb(29,79,64);--nh-ink:rgb(40,40,41);--nh-track:rgba(40,40,41,.10);' +
      'box-sizing:border-box;padding:16px 20px 44px;border-bottom:1px solid rgba(40,40,41,.08);}' +
      '.nh-ms *{box-sizing:border-box;}' +
      '.nh-ms__headline{margin:0 0 16px;font-size:12px;line-height:1.35;font-weight:700;' +
      'letter-spacing:.02em;text-transform:uppercase;color:var(--nh-ink);}' +
      '.nh-ms__headline strong{font-weight:700;color:var(--nh-accent);}' +
      '.nh-ms__track{position:relative;height:8px;border-radius:9999px;background:var(--nh-track);}' +
      '.nh-ms__fill{position:absolute;left:0;top:0;height:100%;border-radius:9999px;' +
      'background:var(--nh-accent);transition:width .45s ease;}' +
      '.nh-ms__nodes{position:absolute;inset:0;}' +
      '.nh-ms__node{position:absolute;top:50%;}' +
      '.nh-ms__dot{position:absolute;top:0;left:0;transform:translate(-50%,-50%);width:16px;height:16px;' +
      'border-radius:50%;background:#fff;border:2px solid var(--nh-track);transition:all .3s ease;}' +
      '.nh-ms__node.is-reached .nh-ms__dot{background:var(--nh-accent);border-color:var(--nh-accent);}' +
      '.nh-ms__label{position:absolute;top:15px;max-width:78px;white-space:normal;line-height:1.15;' +
      'font-size:9px;font-weight:700;letter-spacing:.02em;text-transform:uppercase;color:var(--nh-ink);' +
      'opacity:.55;transition:opacity .3s ease;}' +
      '.nh-ms__node.is-reached .nh-ms__label{opacity:1;color:var(--nh-accent);}' +
      '.nh-ms__reward{display:block;}' +
      '.nh-ms__reward + .nh-ms__reward{margin-top:1px;}' +
      '.nh-ms__node[data-align="center"] .nh-ms__label{left:0;transform:translateX(-50%);text-align:center;}' +
      '.nh-ms__node[data-align="left"] .nh-ms__label{left:0;text-align:left;}' +
      '.nh-ms__node[data-align="right"] .nh-ms__label{right:0;left:auto;text-align:right;}' +
      // Keeping the drawer's footer reachable once the bar is prepended.
      //
      // The mini-cart's inner wrapper is sized straight off the viewport —
      // `h-[calc(100dvh_-_60px)] md:h-[calc(100dvh_-_96px)]` (and
      // `md:h-[calc(100dvh_-_104px)]` when the cart is empty). Those offsets
      // only account for the drawer's own header, so prepending the bar pushes
      // that wrapper — and with it the subtotal, points banner and CHECKOUT
      // button — below the dialog by exactly the bar's height. That region is
      // not scrollable, so the button can't be reached at all.
      //
      // Shrinking <mini-cart> alone does NOT help: the inner wrapper's height
      // comes from dvh, not from its parent, so it keeps overflowing. The
      // wrapper itself has to stop being viewport-sized and become a flex child
      // of a definite-height column instead. It is already `flex flex-col`
      // internally, so its own scroll area absorbs the difference.
      //
      // Matched on [class*="100dvh"] rather than the escaped Tailwind class
      // names so it survives the arbitrary-value offsets changing.
      // Scoped to .nh-ms-host, set only on the drawer mount, so the cart page
      // keeps its own layout.
      '.nh-ms-host{display:flex;flex-direction:column;}' +
      '.nh-ms-host > .nh-ms{flex:0 0 auto;}' +
      '.nh-ms-host > mini-cart{flex:1 1 auto;min-height:0;height:auto;' +
      'display:flex;flex-direction:column;}' +
      '.nh-ms-host mini-cart [class*="100dvh"]{height:auto !important;' +
      'max-height:none !important;flex:1 1 auto;min-height:0;}';
    var el = document.createElement('style');
    el.id = STYLE_ID;
    el.textContent = css;
    document.head.appendChild(el);
  }

  // Where the bar mounts. These wrappers are server-rendered Liquid, outside
  // the Preact-controlled subtree, so a prepended child stays put.
  // fixHeight: the host is a fixed-height (100vh) box whose child fills it, so
  // it needs the flex-column treatment from injectStyle() to stay reachable.
  var MOUNTS = [
    { host: '#MiniCartSlideout .js-content-modal-dialog', id: 'nh-ms-drawer', fixHeight: true },
    { host: 'cart-page', id: 'nh-ms-page' }
  ];

  var lastSubtotal = null;

  function render(subtotalCents) {
    lastSubtotal = subtotalCents;
    // Rebuild every render: BOGOs offers load async and can change as the
    // cart changes (an offer can become claimable mid-session).
    MILES = buildMilestones();
    if (!MILES.length) {
      // Nothing to show any more (say the fallback gift gave way to "no
      // offers" and no shipping threshold is set): drop a bar already drawn.
      MOUNTS.forEach(function (m) {
        var stale = document.getElementById(m.id);
        if (stale) stale.parentNode.removeChild(stale);
      });
      return;
    }
    injectStyle();
    var html = buildHTML(subtotalCents);
    MOUNTS.forEach(function (m) {
      var host = document.querySelector(m.host);
      if (!host) return;
      if (m.fixHeight) host.classList.add('nh-ms-host');
      var bar = document.getElementById(m.id);
      if (!bar) {
        bar = document.createElement('div');
        bar.id = m.id;
        bar.className = 'nh-ms';
        host.insertBefore(bar, host.firstChild);
      }
      bar.innerHTML = html;
    });
  }

  function readSubtotal(cart) {
    // total_price = subtotal after cart-level discounts, in cents.
    if (cart && typeof cart.total_price === 'number') return cart.total_price;
    return 0;
  }

  function refresh() {
    fetch(GM_STATE.routes && GM_STATE.routes.cart_url ? GM_STATE.routes.cart_url + '.js' : '/cart.js',
      { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { render(readSubtotal(cart)); })
      .catch(function () { /* keep last render */ });
  }

  // Detect cart mutations by wrapping fetch + XHR for any /cart/ endpoint.
  function isCartWrite(url) {
    return typeof url === 'string' &&
      /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(url);
  }

  var _fetch = window.fetch;
  if (_fetch) {
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url);
      var p = _fetch.apply(this, arguments);
      if (isCartWrite(url)) p.then(function () { setTimeout(refresh, 60); }).catch(function () {});
      return p;
    };
  }

  var _open = XMLHttpRequest.prototype.open;
  var _send = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__nhCartWrite = isCartWrite(url);
    return _open.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function () {
    if (this.__nhCartWrite) {
      this.addEventListener('load', function () { setTimeout(refresh, 60); });
    }
    return _send.apply(this, arguments);
  };

  // BOGOs' script tag loads async, so its offers usually aren't on the page
  // yet at init. Poll briefly and re-render whenever what it reports changes:
  // first loading (the fallback gives way to the live offers, or to nothing),
  // then offers landing if BOGOs fills the list after creating it. Stops once
  // there are offers, or after ~10s.
  function giftKey() { return JSON.stringify(giftMilestonesFromBogos()); }

  function watchForBogos() {
    var seen = giftKey();
    var tries = 0;
    var timer = setInterval(function () {
      tries++;
      var key = giftKey();
      if (key !== seen) {
        seen = key;
        if (lastSubtotal != null) render(lastSubtotal);
      }
      if ((key !== 'null' && key !== '[]') || tries > 40) clearInterval(timer);
    }, 250);
  }

  function init() {
    // First paint from the cart JSON already embedded in GM_STATE, then a
    // fresh /cart.js to correct anything stale.
    var initCart = GM_STATE.cart && GM_STATE.cart.initCart;
    render(readSubtotal(initCart));
    refresh();
    watchForBogos();
    // Re-assert the bar if the drawer host mounts/re-mounts after load.
    var obs = new MutationObserver(function () {
      var need = MOUNTS.some(function (m) {
        return document.querySelector(m.host) && !document.getElementById(m.id);
      });
      if (need && lastSubtotal != null) render(lastSubtotal);
    });
    obs.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
